import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../store';
import type { FamilyMemberPayload } from '../../../../../common';

export default function useTreatmentPlanV3FamilyMembers() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const familyMembers = useSelector(
    (state: RootState) => state.treatmentPlanV3.familyPage?.family_members ?? [],
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.treatmentPlanV3.createFamilyMemberLoading ||
      state.treatmentPlanV3.updateFamilyMemberLoading ||
      state.treatmentPlanV3.deleteFamilyMemberLoading,
  );

  const create = useCallback(
    async (data: FamilyMemberPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        treatmentPlanV3Actions.createTreatmentPlanV3FamilyMember({
          treatmentPlanId,
          data,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.createTreatmentPlanV3FamilyMember.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (memberId: number, data: FamilyMemberPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        treatmentPlanV3Actions.updateTreatmentPlanV3FamilyMember({
          treatmentPlanId,
          memberId,
          data,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.updateTreatmentPlanV3FamilyMember.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (memberId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        treatmentPlanV3Actions.deleteTreatmentPlanV3FamilyMember({
          treatmentPlanId,
          memberId,
          getAccessTokenSilently,
        }),
      );
      return treatmentPlanV3Actions.deleteTreatmentPlanV3FamilyMember.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return { familyMembers, isSaving, create, update, remove };
}
